// MARK://dhk8/admin-shell/pages/modules/FloorQrCodesPage
// Gestión de QR de mesa → dhk8-pos-floor. Cada mesa del plano lleva un `qrCode`
// (token opaco). El comensal lo escanea con la cámara → abre el guest PWA
// (`guest.nintaigo.com/r/<slug>/t/<qrCode>`) → check-in en la mesa.
// Esta pantalla: ver el QR de cada mesa, regenerarlo, y descargar/imprimir el
// SVG para pegarlo físicamente en la mesa.
import { useEffect, useMemo, useState } from 'react'
import QRCode from 'qrcode'
import { QrCode as QrIcon, RefreshCw, Download, Printer } from 'lucide-react'
import * as floor from '../../lib/clients/floor.js'
import { loadBranding } from '../../lib/branding-loader.js'
import { useAsync, asyncState } from '../../lib/use-async.js'

const GUEST_BASE = 'https://guest.nintaigo.com'
// 2026-05-24: el slug del tenant se carga dinamicamente desde /v1/me/branding
// (campo `brand`). Si falla o el tenant aun no tiene brand seteado, el input
// queda vacio y el operador puede capturarlo manualmente. ANTES era hardcoded
// "cafecito-de-prueba-test" — placeholder del demo que llego a producción.
const PLACEHOLDER_HINT = 'mi-restaurante'

/** Deep-link que codifica el QR físico de la mesa. */
function tableUrl(slug: string, qrCode: string): string {
  return `${GUEST_BASE}/r/${slug}/t/${qrCode}`
}

/** Yield to the event loop so a long QR-encoding batch never blocks the UI. */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve())
    else setTimeout(resolve, 0)
  })
}

export default function FloorQrCodesPage() {
  // Sucursal de los QR: se elige, se ve y se recuerda. Sin esto el operador
  // imprimía QR sin saber de qué local eran.
  const SUC_KEY = 'nintaigo-admin-qr-branch'
  const [sucursalSel, setSucursalSel] = useState<string | null>(() => {
    try { return localStorage.getItem(SUC_KEY) } catch { return null }
  })
  const [sucursales, setSucursales] = useState<{ branchId: string; nombre: string }[]>([])
  useEffect(() => {
    void (async () => {
      try {
        const bs = await floor.listFloorBranches()
        setSucursales(bs)
        if (sucursalSel === null && bs.length > 0) setSucursalSel(bs[0]!.branchId)
      } catch { /* sin planos → sin selector */ }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  function elegirSucursal(id: string): void {
    setSucursalSel(id)
    try { localStorage.setItem(SUC_KEY, id) } catch { /* privado */ }
  }
  const list = useAsync((s) => floor.listTableQrCodes(s, sucursalSel), [sucursalSel])
  const [slug, setSlug] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [svgs, setSvgs] = useState<Record<string, string>>({})

  // Cargar slug del tenant desde /v1/me/branding (campo `brand`).
  useEffect(() => {
    void (async () => {
      try {
        const b = await loadBranding()
        if (b && typeof b.brand === 'string' && b.brand.length > 0) {
          setSlug(b.brand)
        }
      } catch { /* silent: el operador captura manualmente */ }
    })()
  }, [])

  // IMPORTANT: keep `tables` reference-stable. `list.data ?? []` would mint a
  // brand-new array on every render — and since the QR-render effect below
  // depends on it, that produced an infinite render→effect→setState loop while
  // the request was in-flight (loading state), freezing the whole tab. Anchor
  // on `list.data` (a stable ref once loaded; null while loading) instead.
  const tables = useMemo<floor.TableQr[]>(() => list.data ?? [], [list.data])

  // Render a QR SVG for every table whenever the list or slug changes.
  // QR encoding (`qrcode`) is CPU-bound and runs synchronously inside its
  // Promise; encoding a whole floor in one tick blocks the main thread. We
  // encode one QR at a time, yielding a frame between each and flushing
  // partial results incrementally so the screen stays responsive.
  useEffect(() => {
    const withQr = tables.filter((t) => t.qrCode)
    if (withQr.length === 0) { setSvgs({}); return }
    let alive = true
    const slugClean = slug.trim()
    if (slugClean.length === 0) { setSvgs({}); return }
    void (async () => {
      const acc: Record<string, string> = {}
      for (const t of withQr) {
        if (!alive) return
        try {
          acc[t.tableId] = await QRCode.toString(tableUrl(slugClean, t.qrCode as string), {
            type: 'svg', errorCorrectionLevel: 'M', margin: 2,
            color: { dark: '#0c1f12', light: '#ffffff' },
          })
        } catch { /* skip a table that fails to encode */ }
        if (!alive) return
        setSvgs({ ...acc })
        await nextFrame()
      }
    })()
    return () => { alive = false }
  }, [tables, slug])

  const assigned = useMemo(() => tables.filter((t) => t.qrCode).length, [tables])

  async function regenerate(tableId: string) {
    if (!confirm('¿Regenerar el QR de esta mesa? El QR impreso anterior dejará de funcionar.')) return
    setBusyId(tableId)
    try { await floor.regenerateTableQr(tableId); list.reload() }
    catch (e) { alert(e instanceof Error ? e.message : 'No se pudo regenerar el QR.') }
    finally { setBusyId(null) }
  }

  function download(tableId: string, code: string) {
    const svg = svgs[tableId]
    if (!svg) return
    const blob = new Blob([svg], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `qr-${code.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.svg`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  function printSheet() {
    const slugClean = slug.trim()
    if (slugClean.length === 0) { alert('Captura el slug del negocio antes de imprimir.'); return }
    const cards = tables.filter((t) => t.qrCode).map((t) => `
      <div class="card">
        <div class="qr">${svgs[t.tableId] ?? ''}</div>
        <div class="code">${t.code}</div>
        <div class="hint">Escanea para ordenar</div>
      </div>`).join('')
    const w = window.open('', '_blank', 'width=820,height=1000')
    if (!w) return
    // Print-only stylesheet escrito a una window.open() nueva: los tokens
    // CSS de la app no llegan ahí (otro document). Por eso el radius de 12px
    // queda hardcoded — coincide con --pos-radius-lg de POS. Es una decoración
    // de impresión para guías de corte, no UI de pantalla. (exec-ui-tightening)
    w.document.write(`<!doctype html><html><head><title>QR de mesa · ${slugClean}</title>
      <style>
        body{font-family:system-ui,sans-serif;margin:24px;}
        .grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;}
        .card{border:1px solid #ccc;border-radius:12px;padding:20px;text-align:center;page-break-inside:avoid;}
        .qr svg{width:220px;height:220px;}
        .code{font-size:22px;font-weight:700;margin-top:8px;}
        .hint{font-size:13px;color:#666;}
      </style></head><body><div class="grid">${cards}</div>
      <script>window.onload=function(){window.print()}</script></body></html>`)
    w.document.close()
  }

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div><h1 className="adm-page__title">QR de mesa</h1><p className="adm-page__sub">Genera, regenera, descarga e imprime los QR que el comensal escanea</p></div>
        <div className="adm-page__actions">
          {sucursales.length > 0 && (
            <label className="adm-field" style={{ margin: 0, minWidth: 200 }}>
              <span className="adm-field__label">Sucursal de estos QR</span>
              <select className="adm-input" value={sucursalSel ?? ''} onChange={(e) => elegirSucursal(e.target.value)}>
                {sucursales.map((b) => <option key={b.branchId} value={b.branchId}>{b.nombre}</option>)}
              </select>
            </label>
          )}
          <button className="adm-btn" onClick={printSheet} disabled={assigned === 0}><Printer size={16} /> Imprimir hoja</button>
        </div>
      </div>

      <div className="adm-card adm-card--pad" style={{ marginBottom: 16 }}>
        <label className="adm-field" style={{ maxWidth: 420, marginBottom: 0 }}>
          <span className="adm-field__label">Slug del negocio (en el deep-link del QR)</span>
          <input className="adm-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={PLACEHOLDER_HINT} />
        </label>
        <p className="adm-row__meta" style={{ marginTop: 6 }}>
          El QR codifica <code>{GUEST_BASE}/r/{slug.trim() || '<slug>'}/t/&lt;código&gt;</code>
        </p>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && tables.length === 0 && (
        <div className="adm-card adm-card--pad"><div className="adm-empty">No hay mesas en el plano. Crea mesas en la pantalla de Plano para asignarles QR.</div></div>
      )}

      {list.data && tables.length > 0 && (
        <div className="adm-grid adm-grid--wide" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {tables.map((t) => (
            <div key={t.tableId} className="adm-card adm-card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{t.code}</div>
                  <div className="adm-row__meta">{t.sectionName ?? 'Sin sección'} · {t.capacity} lugares</div>
                </div>
                {t.qrCode
                  ? <span className="adm-badge adm-badge--ok">QR asignado</span>
                  : <span className="adm-badge adm-badge--warn">Sin QR</span>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', padding: 8, background: '#fff', borderRadius: 10, minHeight: 180 }}>
                {t.qrCode && svgs[t.tableId]
                  ? <div style={{ width: 168, height: 168 }} dangerouslySetInnerHTML={{ __html: svgs[t.tableId] }} />
                  : <div className="adm-empty" style={{ display: 'flex', alignItems: 'center' }}><QrIcon size={32} aria-hidden style={{ opacity: 0.3 }} /></div>}
              </div>

              {t.qrCode && (
                <div className="adm-row__meta" style={{ wordBreak: 'break-all', textAlign: 'center' }}>
                  código <code>{t.qrCode}</code>
                </div>
              )}

              <div style={{ display: 'flex', gap: 6 }}>
                <button className="adm-btn adm-btn--sm" style={{ flex: 1 }} disabled={busyId === t.tableId} onClick={() => regenerate(t.tableId)}>
                  <RefreshCw size={14} /> {busyId === t.tableId ? 'Regenerando…' : (t.qrCode ? 'Regenerar' : 'Asignar QR')}
                </button>
                {t.qrCode && (
                  <button className="adm-btn adm-btn--sm" style={{ flex: 1 }} disabled={!svgs[t.tableId]} onClick={() => download(t.tableId, t.code)}>
                    <Download size={14} /> Descargar
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
